import { useEffect, useRef, useState } from 'react'
import { Pause, Play } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { VoiceClip } from './voice-recorder'

function duration(seconds: number) {
  const rounded = Math.round(seconds)
  return `${Math.floor(rounded / 60)}:${String(rounded % 60).padStart(2, '0')}`
}

/** A sent voice message (demo `voice-message.tsx`): playback plus the transcript the assistant read. */
export function VoiceMessage({ clip, outgoing = false }: { clip: VoiceClip; outgoing?: boolean }) {
  const audio = useRef<HTMLAudioElement | null>(null)
  const [playing, setPlaying] = useState(false)
  const [error, setError] = useState('')
  useEffect(() => {
    const player = audio.current
    return () => player?.pause()
  }, [])
  const toggle = () => {
    if (!audio.current) return
    if (playing) audio.current.pause()
    else {
      setError('')
      void audio.current.play().catch(() => {
        setPlaying(false)
        setError('Audio could not play. Try again or read the transcript.')
      })
    }
  }

  return (
    <div
      className={cn(
        'w-full max-w-72 rounded-lg px-3 py-2.5',
        outgoing ? 'bg-primary text-primary-foreground' : 'border bg-muted text-foreground',
      )}
    >
      <div className="flex items-center gap-2.5">
        <button
          aria-label={playing ? 'Pause recording' : 'Play recording'}
          className={cn(
            'grid size-8 shrink-0 place-items-center rounded-full outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50',
            outgoing ? 'bg-primary-foreground text-primary' : 'bg-violet-600 text-white',
          )}
          onClick={toggle}
          type="button"
        >
          {playing ? <Pause className="size-3.5 fill-current" /> : <Play className="ml-0.5 size-3.5 fill-current" />}
        </button>
        <span className="flex-1 text-xs font-medium">Voice message</span>
        <span className={cn('text-xs tabular-nums', outgoing ? 'text-primary-foreground/80' : 'text-muted-foreground')}>
          {duration(clip.durationSeconds)}
        </span>
        <audio
          onEnded={() => setPlaying(false)}
          onError={() => {
            setPlaying(false)
            setError('Audio is unavailable. Read the transcript below.')
          }}
          onPause={() => setPlaying(false)}
          onPlay={() => setPlaying(true)}
          ref={audio}
          src={clip.url}
        />
      </div>
      {error ? (
        <p className="mt-2 text-xs" role="alert">
          {error}
        </p>
      ) : null}
      <details className="mt-2 border-t border-current/15 pt-1.5 text-xs leading-5">
        <summary className="w-fit cursor-pointer font-medium">View transcript</summary>
        <p className={cn('mt-1.5 whitespace-pre-wrap break-words', outgoing ? 'text-primary-foreground/85' : 'text-muted-foreground')}>
          {clip.transcript}
        </p>
      </details>
    </div>
  )
}
